<template>
  <div class="uk-container">
    <template v-if="section !== 'error'">
      <transition name="fade" mode="out-in" @before-enter="beforeEnter">
        <div v-if="!loading">
          <status :appointment="appointment" :visibility="componentData.statusVisibility" />
          <component :is="section" v-bind="componentData" />
        </div>
      </transition>
      <!-- <component :is="section" :data="{ appointment: appointment }" /> -->
    </template>

    <template v-else>
      <div class="uk-alert-danger uk-width-medium uk-margin-auto uk-text-center" uk-alert>
        <p>Oops! <br> Something went wrong.</p>
      </div>
    </template>

    <div id="modal-refund" uk-modal="bg-close: false; esc-close: false;">
      <div class="uk-modal-dialog uk-modal-body uk-text-center">
        <p>Would you like to attach a refund request to this update?</p>
        <p>
          <button class="uk-button uk-button-danger uk-margin-small-right" type="button" @click="refundAction(false)">No</button>
          <button class="uk-button uk-button-primary" type="button" @click="refundAction(true)">Yes</button>
        </p>
      </div>
    </div>

    <div id="modal-update" uk-modal="esc-close: false; bg-close: false;">
      <div class="uk-modal-dialog">
        <div class="uk-modal-body uk-text-center">Appointment details were updated successfully!</div>
        <div class="uk-modal-footer uk-text-center">
          <div class="uk-button uk-button-primary" @click="close()">View Appointment</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Status from '@/components/appointment/Status.vue'
import Dentist from '@/components/appointment/Dentist.vue'
import Payment from '@/components/appointment/Payment.vue'
import Refund from '@/components/Refund.vue'

export default {
  name: 'ViewAppointmentUpdate',

  components: {
    Status,
    Dentist,
    Payment,
    Refund
  },

  data() {
    return {
      loading: true,
      type: null,
      section: null,
      id: null,
      data: {},
      modal: {
        refund: null,
        update: null
      }
    }
  },

  computed: {
    appointment() {
      const { id } = this.$route.params
      const source = this.$store.getters['appointments/find'](id)
      const appointment = id ? ({ ...source }) : null

      if (appointment) {
        const { dentist } = appointment.info
        appointment.id = source.id
        appointment.dentist = {
          bit: appointment.dentist.data[dentist].bit,
          date: appointment.dentist.data[dentist].date,
          id: dentist
        }
      }

      return appointment
    },

    componentData() {
      const { section } = this
      const response = {}

      if (section === 'dentist') {
        response.features = this.appointment.emergency.features
        response.customerLocation = this.appointment.customer.location
      }

      if (section === 'refund') {
        response.amount = (this.appointment.payment.amount / 100).toFixed(2)
        response.refunds = this.appointment.refund ? Object.values(this.appointment.refund) : []
      }

      if (section === 'payment') {
        response.appointment = this.appointment.id
        response.secret = this.data.intent.client_secret
        response.type = 'update'
      }

      return response
    }
  },

  async created() {
    this.$bus.$on('appointment-error', (data) => {
      this.error(data)
    })

    this.$bus.$on('appointment-section-complete', (data) => {
      this.sectionComplete(data)
    })

    this.$bus.$on('refund', (data) => {
      this.sectionComplete({ type: 'refund', data })
    })

    if (this.$route.params.id && this.appointment) {
      this.id = this.$route.params.id
    }

    if (this.$route.query.type) {
      const type = this.validateType(this.$route.query.type) ? this.$route.query.type : null

      if (type) {
        this.type = type
        this.section = type
      } else {
        this.section = 'error'
      }
    }

    if (this.type === 'payment') {
      const loader = this.$loader.show('Loading Payment Information')

      try {
        const retrievePaymentIntent = this.$firebaseFunctions.httpsCallable('retrievePaymentIntent')
        const paymentIntent = await retrievePaymentIntent({ id: this.appointment.payment.id })
        this.data.intent = paymentIntent.data
        loader.hide()
      } catch (error) {
        await loader.hide(true)
        this.$uikit.notification(error, { status: 'danger' })
      }
    }

    this.loading = false
  },

  mounted() {
    this.modal.refund = this.$uikit.modal('#modal-refund')
    this.modal.update = this.$uikit.modal('#modal-update')
  },

  beforeDestroy() {
    this.$bus.$off('appointment-error')
    this.$bus.$off('appointment-section-complete')
    this.$bus.$off('refund')
  },

  methods: {
    error(data) {
      // data.timestamp = this.$moment()
      // this.errors.push(data)

      this.$uikit.notification({
        message: data.message,
        status: data.status,
        timeout: 15000
      })
    },

    validateType(type) {
      return ['dentist', 'payment'].indexOf(type) !== -1
    },

    beforeEnter() {
      document.querySelector('#content').scrollTo(0, 0)
    },

    sectionComplete(section) {
      const { type } = section

      if (type === 'dentist') {
        this.data.dentist = section.data
        this.modal.refund.show()
      }

      if (type === 'refund') {
        this.data.refund = section.data
        this.update()
      }

      if (type === 'payment') {
        // Payment is processed through Stripe and DB updates are handled inside component
        this.update()
      }
    },

    switchSection(section) {
      this.loading = true

      setTimeout(() => {
        this.section = section
      }, 250)

      setTimeout(() => { this.loading = false }, 250)
    },

    refundAction(required) {
      this.modal.refund.hide()

      if (required) {
        this.switchSection('refund')
      } else {
        this.update()
      }
    },

    update() {
      if (this.type === 'dentist') {
        this.updateDentist()
      }

      if (this.type === 'payment') {
        this.modal.update.show()
      }
    },

    async updateDentist() {
      const loader = this.$loader.show('Updating Appointment Dentist Details')
      const updateAppointment = this.$firebaseFunctions.httpsCallable('updateAppointment')
      const data = {
        id: this.appointment.id,
        type: 'dentist',
        data: {
          id: this.data.dentist.selected.dentist,
          date: this.data.dentist.selected.date.t,
          bit: this.data.dentist.selected.bit,
          location: `${this.data.dentist.location.info.outcode} ${this.data.dentist.location.info.incode}`
        }
      }

      if (this.data.refund && this.data.refund.value && this.data.refund.reason) {
        data.refund = this.data.refund
      } else {
        data.refund = false
      }

      try {
        await updateAppointment(data)
        await loader.hide()
        this.modal.update.show()
      } catch (error) {
        loader.hide(true)
        this.$uikit.notification('Something went wrong!', { status: 'danger' })
      }
    },

    close() {
      this.modal.update.hide()
      this.$router.push({ name: 'appointment-single', params: { id: this.appointment.id } })
    }
  }
}
</script>
